import { create } from 'zustand';
import { addAlert } from "./alertsStore";
import { getApiUrl } from "@/utils/apiConfig";
import { useSettingsStore } from "./settingsStore";

const API_URL = getApiUrl();

export type TalkCategory = "CITOYEN" | "ONG" | "PARTI" | "INSTITUTION";
export type TalkContributionStatus = "en_attente" | "valide" | "rejete";
export type TalkDebateStatus = "brouillon" | "planifie" | "publie" | "cloture";

export interface TalkContribution {
  id: string;
  debateId: string;
  authorId: string;
  authorName: string;
  authorCategory: TalkCategory;
  authorColor: string;
  authorShade: "clair" | "fonce";
  genre?: "homme" | "femme" | "non_precise";
  text: string;
  likes: number;
  dislikes?: number;
  status: TalkContributionStatus;
  createdAt: string;
  validatedAt?: string;
  preferredLanguage?: string;
  proposedQuestion?: string;
  proposedDomain?: string;
  authorActivity?: string;
  textFormat?: string;
}

export interface TalkDebate {
  id: string;
  title: string;
  titleAr?: string;
  titleZgh?: string;
  description: string;
  descriptionAr?: string;
  descriptionZgh?: string;
  status: TalkDebateStatus;
  createdAt: string;
  closedAt?: string;
  scheduledDate?: string;
  publishedDate?: string;
  showStats: boolean;
  showStats_participants?: boolean;
  showStats_gender?: boolean;
  showStats_likes?: boolean;
  periodYear?: number;
  periodMonth?: number;
  periodWeek?: number;
  adminConclusion?: string;
}

export interface TalkReport {
  id: string;
  debateId: string;
  generatedAt: string;
  sujet_debat: string;
  synthese_globale: string;
  statistiques_participation: {
    total_contributions: number;
    repartition_genre: { hommes: number; femmes: number; non_precise: number; };
    repartition_acteurs: { citoyens: number; ong: number; partis_politiques: number; institutions: number; };
  };
  proposition_phare: { titre: string; auteur: string; description: string; nombre_likes: number; };
  analyse_par_categorie: any;
  recommandations_actionnables: string[];
}

interface TalkStore {
  debates: TalkDebate[];
  contributions: TalkContribution[];
  reports: TalkReport[];
  interactions: Record<string, 'like' | 'dislike'>;
  isLoading: boolean;
  likesEnabled: boolean;
  
  getOrCreateClientId: () => string;
  fetchDebates: () => Promise<void>;
  fetchContributions: () => Promise<void>;
  fetchInteractions: () => Promise<void>;
  
  submitContribution: (data: Omit<TalkContribution, "id" | "likes" | "status" | "createdAt">) => Promise<void>;
  createDebate: (debate: Omit<TalkDebate, "id" | "createdAt">) => Promise<void>;
  updateDebate: (id: string, updates: Partial<TalkDebate>) => Promise<void>;
  
  adminValidateContribution: (id: string, validate: boolean) => Promise<void>;
  adminEditContributionText: (id: string, newText: string, textFormat?: string) => Promise<void>;
  
  toggleTalkContributionInteraction: (id: string, newType: 'like' | 'dislike' | 'none') => Promise<void>;
  setTalkLikesEnabled: (enabled: boolean) => void;
  
  saveReport: (report: TalkReport) => void;
  deleteDebate: (id: string) => Promise<void>;
  deleteContribution: (id: string) => Promise<void>;
  adminUpdateContribution: (id: string, updates: Partial<TalkContribution>) => Promise<void>;
}

const STORE_KEYS = {
  reports: "kaynin_talk_reports",
  likesEnabled: "kaynin_talk_likes_enabled",
  interactions: "kaynin_talk_interactions",
  clientId: "kaynin_client_id"
};

export const useTalkStore = create<TalkStore>((set, get) => ({
  debates: [],
  contributions: [],
  reports: (() => {
    const cached = useSettingsStore.getState().getSetting(STORE_KEYS.reports, "[]");
    try { return typeof cached === "string" ? JSON.parse(cached) : cached; } catch { return []; }
  })(),
  interactions: JSON.parse(localStorage.getItem(STORE_KEYS.interactions) || "{}"),
  isLoading: false,
  likesEnabled: useSettingsStore.getState().getSetting(STORE_KEYS.likesEnabled, true) as boolean,

  getOrCreateClientId: () => {
    let clientId = localStorage.getItem(STORE_KEYS.clientId);
    if (!clientId) {
      clientId = 'anon_' + Math.random().toString(36).substr(2, 9) + Date.now();
      localStorage.setItem(STORE_KEYS.clientId, clientId);
    }
    return clientId;
  },

  fetchDebates: async () => {
    set({ isLoading: true });
    try {
      const res = await fetch(`${API_URL}/talk_debates.php`);
      if (res.ok) {
        const data = await res.json();
        set({ debates: Array.isArray(data) ? data : [] });
      }
    } catch (e) {
      console.error("Error fetching debates", e);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchContributions: async () => {
    set({ isLoading: true });
    try {
      const res = await fetch(`${API_URL}/talk_contributions.php`);
      if (res.ok) {
        const data = await res.json();
        set({ contributions: Array.isArray(data) ? data : [] });
      }
    } catch (e) {
      console.error("Error fetching contributions", e);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchInteractions: async () => {
    // Mostly handled via local storage for current user, but could sync from server
  },

  submitContribution: async (data) => {
    const newContrib: TalkContribution = {
      ...data,
      id: "cont_" + Math.random().toString(36).substr(2, 9),
      likes: 0,
      status: "en_attente",
      createdAt: new Date().toISOString()
    };
    
    try {
      const res = await fetch(`${API_URL}/talk_contributions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newContrib)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Erreur serveur HTTP " + res.status);
      }
      
      // Update UI only on success
      set(state => ({ contributions: [newContrib, ...state.contributions] }));
    } catch (e: any) {
      console.error("Error submitting contribution:", e);
      throw e;
    }

  },

  createDebate: async (debate) => {
    const newDebate: TalkDebate = {
      ...debate,
      id: "debate_" + Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    
    try {
      const res = await fetch(`${API_URL}/talk_debates.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDebate)
      });
      if (!res.ok) throw new Error("Failed to create debate");
      set(state => ({ debates: [newDebate, ...state.debates] }));
    } catch (e) {
      console.error("Error creating debate:", e);
      throw e;
    }
  },

  updateDebate: async (id, updates) => {
    try {
      const debate = get().debates.find(d => d.id === id);
      if (debate) {
        const updatedDebate = { ...debate, ...updates };
        const res = await fetch(`${API_URL}/talk_debates.php`, {
          method: 'POST', // backend handles PUT as POST
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedDebate)
        });
        if (!res.ok) throw new Error("Failed to update debate");
        set(state => ({
          debates: state.debates.map(d => d.id === id ? updatedDebate : d)
        }));
      }
    } catch (e) {
      console.error("Error updating debate:", e);
      throw e;
    }
  },

  adminValidateContribution: async (id, validate) => {
    const status = validate ? "valide" : "rejete";
    try {
      const contrib = get().contributions.find(c => c.id === id);
      if (contrib) {
        const updatedContrib = { ...contrib, status, validatedAt: validate ? new Date().toISOString() : contrib.validatedAt };
        const res = await fetch(`${API_URL}/talk_contributions.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedContrib)
        });
        if (!res.ok) throw new Error("Failed to validate contribution");
        set(state => ({
          contributions: state.contributions.map(c => c.id === id ? updatedContrib : c)
        }));
      }
    } catch (e) {
      console.error("Error validating contribution:", e);
      throw e;
    }
  },

  adminEditContributionText: async (id, newText, textFormat) => {
    try {
      const contrib = get().contributions.find(c => c.id === id);
      if (contrib) {
        const updatedContrib = { ...contrib, text: newText, textFormat: textFormat !== undefined ? textFormat : contrib.textFormat };
        const res = await fetch(`${API_URL}/talk_contributions.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedContrib)
        });
        if (!res.ok) throw new Error("Failed to edit contribution");
        set(state => ({
          contributions: state.contributions.map(c => c.id === id ? updatedContrib : c)
        }));
      }
    } catch (e) {
      console.error("Error editing contribution:", e);
      throw e;
    }
  },

  toggleTalkContributionInteraction: async (id, newType) => {
    const { interactions, contributions, getOrCreateClientId } = get();
    const currentType = interactions[id] || 'none';
    if (currentType === newType) return;
    
    const newInteractions = { ...interactions };
    if (newType === 'none') {
      delete newInteractions[id];
    } else {
      newInteractions[id] = newType;
    }
    
    // Optimistic UI updates
    set(state => ({
      interactions: newInteractions,
      contributions: state.contributions.map(c => {
        if (c.id !== id) return c;
        let newLikes = c.likes || 0;
        let newDislikes = c.dislikes || 0;
        
        // Undo old
        if (currentType === 'like') newLikes = Math.max(0, newLikes - 1);
        if (currentType === 'dislike') newDislikes = Math.max(0, newDislikes - 1);
        
        // Apply new
        if (newType === 'like') newLikes++;
        if (newType === 'dislike') newDislikes++;
        
        // Alert
        if (newType === 'like' && currentType !== 'like' && c.authorId && !c.authorId.startsWith('ong_')) {
          addAlert({
             title: { fr: "Mention J'aime ❤️", ar: "إعجاب ❤️", zgh: "ⵜⴰⵢⵔⵉ ❤️" },
             description: { 
                fr: `Quelqu'un a aimé votre contribution : "${c.text.substring(0, 30)}..."`, 
                ar: `شخص ما أعجب بمساهمتك : "${c.text.substring(0, 30)}..."`, 
                zgh: `ⵉⵄⵊⴱ ⵉ ⵢⴰⵏ ⵜⵎⵔⵔⵓⵜ ⵏⵏⴽ : "${c.text.substring(0, 30)}..."` 
             },
             sourceName: "Kayn'in Talk",
             sourceType: "Interaction",
             region: "Toutes",
             theme: "Politique",
             isImportant: false,
             targetUserId: c.authorId,
             notificationType: "talk_reply"
          });
        }
        
        return { ...c, likes: newLikes, dislikes: newDislikes };
      })
    }));
    
    localStorage.setItem(STORE_KEYS.interactions, JSON.stringify(newInteractions));
    
    try {
      await fetch(`${API_URL}/talk_interactions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contribution_id: id,
          user_id: getOrCreateClientId(),
          type: newType
        })
      });
      // NOTE: backend updates the interaction table, but we should also update the contribution like count in the DB.
      // Since talk_contributions.php currently takes the whole contribution object on POST, we re-post it.
      const updatedContrib = get().contributions.find(c => c.id === id);
      if (updatedContrib) {
        await fetch(`${API_URL}/talk_contributions.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedContrib)
        });
      }
    } catch (e) {
      console.error("Error toggling interaction:", e);
    }
  },

  setTalkLikesEnabled: (enabled) => {
    set({ likesEnabled: enabled });
    useSettingsStore.getState().updateSetting(STORE_KEYS.likesEnabled, enabled);
  },

  saveReport: (report) => {
    set(state => {
      const existingIdx = state.reports.findIndex(r => r.debateId === report.debateId);
      let newReports = [...state.reports];
      if (existingIdx >= 0) newReports[existingIdx] = report;
      else newReports.push(report);
      useSettingsStore.getState().updateSetting(STORE_KEYS.reports, JSON.stringify(newReports));
      return { reports: newReports };
    });
  },

  deleteDebate: async (id) => {
    try {
      const res = await fetch(`${API_URL}/talk_debates.php?id=${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error("Failed to delete debate");
      set(state => ({ debates: state.debates.filter(d => d.id !== id) }));
    } catch (e) {
      console.error("Error deleting debate:", e);
      throw e;
    }
  },

  deleteContribution: async (id) => {
    try {
      const res = await fetch(`${API_URL}/talk_contributions.php?id=${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error("Failed to delete contribution");
      set(state => ({ contributions: state.contributions.filter(c => c.id !== id) }));
    } catch (e) {
      console.error("Error deleting contribution:", e);
      throw e;
    }
  },

  adminUpdateContribution: async (id, updates) => {
    try {
      const contrib = get().contributions.find(c => c.id === id);
      if (contrib) {
        const updatedContrib = { ...contrib, ...updates };
        const res = await fetch(`${API_URL}/talk_contributions.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedContrib)
        });
        if (!res.ok) throw new Error("Failed to update contribution");
        set(state => ({
          contributions: state.contributions.map(c => c.id === id ? updatedContrib : c)
        }));
      }
    } catch (e) {
      console.error("Error updating contribution:", e);
      throw e;
    }
  }
}));

// Backwards compatibility wrappers
export const getAllDebates = () => useTalkStore.getState().debates;
export const getAllContributions = () => useTalkStore.getState().contributions;
export const getValidatedContributions = (debateId?: string) => {
  let all = useTalkStore.getState().contributions.filter(c => c.status === "valide");
  if (debateId) all = all.filter(c => c.debateId === debateId);
  return all;
};
export const getPendingContributions = () => useTalkStore.getState().contributions.filter(c => c.status === "en_attente");
export const submitContribution = (data: any) => useTalkStore.getState().submitContribution(data);
export const createDebate = (data: any) => useTalkStore.getState().createDebate(data);
export const updateDebate = (id: string, updates: any) => useTalkStore.getState().updateDebate(id, updates);
export const adminValidateContribution = (id: string, validate: boolean) => useTalkStore.getState().adminValidateContribution(id, validate);
export const adminEditContributionText = (id: string, text: string, textFormat?: string) => useTalkStore.getState().adminEditContributionText(id, text, textFormat);
export const toggleTalkContributionInteraction = (id: string, type: any) => useTalkStore.getState().toggleTalkContributionInteraction(id, type);
export const getTalkInteractions = () => useTalkStore.getState().interactions;
export const isTalkLikesEnabled = () => useTalkStore.getState().likesEnabled;
export const setTalkLikesEnabled = (enabled: boolean) => useTalkStore.getState().setTalkLikesEnabled(enabled);
export const getAllReports = () => useTalkStore.getState().reports;
export const getReportForDebate = (id: string) => useTalkStore.getState().reports.find(r => r.debateId === id);
export const saveReport = (report: any) => useTalkStore.getState().saveReport(report);
export const deleteDebate = (id: string) => useTalkStore.getState().deleteDebate(id);
export const deleteContribution = (id: string) => useTalkStore.getState().deleteContribution(id);
export const adminUpdateContribution = (id: string, updates: any) => useTalkStore.getState().adminUpdateContribution(id, updates);
